import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Alert, AlertTitle, Button, Typography } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { reportBoundaryError } from '../services/errorReporter';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary Component
 * Catches JavaScript errors in child component tree and displays fallback UI
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
    // P2: silent capture so admins see the boundary crash in /events.
    // The reporter throttles + catches its own failures.
    reportBoundaryError({
      errorName: error.name,
      errorMessage: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack ?? undefined,
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const isDevelopment = process.env.NODE_ENV === 'development';

      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          p={3}
          bgcolor="background.default"
        >
          <Box maxWidth={600}>
            <Alert severity="error">
              <AlertTitle>Etwas ist schiefgelaufen</AlertTitle>
              <Typography variant="body2" mb={2}>
                Die Anwendung hat einen unerwarteten Fehler festgestellt.
              </Typography>

              {isDevelopment && this.state.error && (
                <Box
                  mb={2}
                  p={2}
                  bgcolor="rgba(0,0,0,0.05)"
                  borderRadius={1}
                  sx={{ overflowX: 'auto' }}
                >
                  <Typography variant="caption" fontFamily="monospace">
                    {this.state.error.toString()}
                  </Typography>
                  {this.state.errorInfo && (
                    <Typography
                      variant="caption"
                      fontFamily="monospace"
                      component="pre"
                      sx={{ fontSize: '0.7rem', mt: 1 }}
                    >
                      {this.state.errorInfo.componentStack}
                    </Typography>
                  )}
                </Box>
              )}

              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleReload}
                >
                  Seite neu laden
                </Button>
                <Button variant="outlined" onClick={this.handleReset}>
                  Fehler zurücksetzen
                </Button>
              </Box>
            </Alert>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
